<template>
    <Teleport to="body">
        <div
            v-if="showModal"
            class="fixed inset-0 z-50 overflow-y-auto"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
        >
            <!-- Backdrop -->
            <div
                class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                @click="closeModal"
            ></div>

            <!-- Modal Panel -->
            <div class="flex min-h-screen items-center justify-center p-4">
                <div
                    class="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
                >
                    <!-- Modal Header -->
                    <div
                        class="bg-white dark:bg-gray-800 px-4 pb-4 pt-5 sm:p-6 sm:pb-4"
                    >
                        <div class="sm:flex sm:items-start">
                            <div
                                class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900 sm:mx-0 sm:h-10 sm:w-10"
                            >
                                <svg
                                    class="h-6 w-6 text-primary-600 dark:text-primary-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                                    />
                                </svg>
                            </div>
                            <div
                                class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left"
                            >
                                <h3
                                    class="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                                    id="modal-title"
                                >
                                    Import Questions
                                </h3>
                                <div class="mt-2">
                                    <p
                                        class="text-sm text-gray-500 dark:text-gray-400"
                                    >
                                        Upload an Excel file (.xlsx) to import
                                        questions. The file should contain the
                                        following columns:
                                    </p>
                                    <ul
                                        class="mt-2 text-xs text-gray-500 dark:text-gray-400 list-disc list-inside"
                                    >
                                        <li>
                                            KODE MATA PELAJARAN (Subject Code)
                                        </li>
                                        <li>NIP TEACHER</li>
                                        <li>QUESTION</li>
                                        <li>
                                            TYPE (essay, true_false,
                                            multiple_choice, multiple_select)
                                        </li>
                                        <li>POIN (Points)</li>
                                        <li>OPTIONS (separated by |)</li>
                                        <li>CORRECT ANSWER</li>
                                        <li>EXPLANATION/PENJELASAN</li>
                                        <li>TAGS (separated by comma)</li>
                                        <li>
                                            TINGKAT KESULITAN (easy, medium,
                                            hard)
                                        </li>
                                        <li>STATUS (true/false or 1/0)</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Modal Body -->
                    <div class="px-4 py-4 sm:px-6">
                        <div class="mt-4">
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Select Excel File
                            </label>
                            <input
                                ref="fileInput"
                                type="file"
                                accept=".xlsx,.xls"
                                @change="handleFileChange"
                                class="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 dark:file:bg-primary-900 dark:file:text-primary-200 hover:file:bg-primary-100 dark:hover:file:bg-primary-800 cursor-pointer"
                            />
                            <p
                                class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                            >
                                Maximum file size: 10MB. Supported formats:
                                .xlsx, .xls
                            </p>
                        </div>

                        <!-- Download Template -->
                        <div
                            class="mt-4 rounded-md bg-success-50 dark:bg-success-900/20 p-4"
                        >
                            <h4
                                class="mb-2 text-sm font-medium text-success-800 dark:text-success-200"
                            >
                                Need a template?
                            </h4>
                            <p
                                class="mb-3 text-xs text-success-700 dark:text-success-300"
                            >
                                Download Excel template to see required format
                            </p>
                            <button
                                type="button"
                                @click="downloadTemplate"
                                class="inline-flex items-center rounded-md border border-transparent bg-success-100 px-3 py-2 text-xs font-medium text-success-700 hover:bg-success-200 dark:bg-success-800 dark:text-success-100 dark:hover:bg-success-700"
                            >
                                <svg
                                    class="mr-2 h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                    />
                                </svg>
                                Download Template
                            </button>
                        </div>

                        <!-- Error Message -->
                        <div
                            v-if="errorMessage"
                            class="mt-4 rounded-md bg-danger-50 dark:bg-danger-900/20 p-4"
                        >
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <svg
                                        class="h-5 w-5 text-danger-400"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div class="ml-3 w-full">
                                    <h3
                                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                                    >
                                        {{ errorMessage }}
                                    </h3>

                                    <!-- Structured Error List -->
                                    <div
                                        v-if="errorRows.length > 0"
                                        class="mt-3 max-h-48 overflow-y-auto"
                                    >
                                        <table
                                            class="w-full text-xs text-danger-700 dark:text-danger-300 border border-danger-200 dark:border-danger-800 rounded"
                                        >
                                            <thead>
                                                <tr
                                                    class="bg-danger-100 dark:bg-danger-900/40"
                                                >
                                                    <th
                                                        class="px-2 py-1.5 text-left font-semibold"
                                                    >
                                                        Baris
                                                    </th>
                                                    <th
                                                        class="px-2 py-1.5 text-left font-semibold"
                                                    >
                                                        Kolom
                                                    </th>
                                                    <th
                                                        class="px-2 py-1.5 text-left font-semibold"
                                                    >
                                                        Error
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr
                                                    v-for="(
                                                        row, idx
                                                    ) in errorRows"
                                                    :key="idx"
                                                    class="border-t border-danger-200 dark:border-danger-800"
                                                >
                                                    <td
                                                        class="px-2 py-1.5 font-medium whitespace-nowrap"
                                                    >
                                                        {{ row.lokasi_row }}
                                                    </td>
                                                    <td
                                                        class="px-2 py-1.5 font-medium whitespace-nowrap"
                                                    >
                                                        {{
                                                            row.lokasi_column ||
                                                            "-"
                                                        }}
                                                    </td>
                                                    <td class="px-2 py-1.5">
                                                        <ul
                                                            class="list-disc list-inside"
                                                        >
                                                            <li
                                                                v-for="(
                                                                    err, eIdx
                                                                ) in row.errors"
                                                                :key="eIdx"
                                                            >
                                                                {{ err }}
                                                            </li>
                                                        </ul>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Modal Footer -->
                    <div
                        class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
                    >
                        <button
                            type="button"
                            @click="handleImport"
                            :disabled="!selectedFile || loading"
                            class="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <span v-if="loading" class="flex items-center">
                                <svg
                                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        class="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="4"
                                    ></circle>
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Importing...
                            </span>
                            <span v-else>Import</span>
                        </button>
                        <button
                            type="button"
                            @click="closeModal"
                            class="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-600 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:w-auto transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { questionBankApi } from "@/api/questionBank";

interface ImportErrorRow {
    lokasi_row: number;
    lokasi_column: string;
    errors: string[];
}

const props = defineProps({
    showModal: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits<{
    (e: "close"): void;
    (e: "imported"): void;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const loading = ref(false);
const errorMessage = ref("");
const errorRows = ref<ImportErrorRow[]>([]);

const handleFileChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
        const validExtensions = [".xlsx", ".xls"];
        const fileExtension = file.name
            .substring(file.name.lastIndexOf("."))
            .toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            errorMessage.value =
                "Please select a valid Excel file (.xlsx, .xls).";
            selectedFile.value = null;
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            errorMessage.value = "File size must be less than 10MB.";
            selectedFile.value = null;
            return;
        }

        selectedFile.value = file;
        clearErrors();
    }
};

const clearErrors = () => {
    errorMessage.value = "";
    errorRows.value = [];
};

const handleImport = async () => {
    if (!selectedFile.value) {
        errorMessage.value = "Please select a file to import.";
        return;
    }

    loading.value = true;
    clearErrors();

    try {
        const formData = new FormData();
        formData.append("file", selectedFile.value);

        const response = await questionBankApi.importQuestions(formData);

        if (response.success) {
            emit("imported");
            closeModal();
        } else {
            setErrorResponse(response.message, response.data);
        }
    } catch (err: any) {
        console.error("Import error:", err);

        if (err.response) {
            const responseData = err.response.data;

            if (
                err.response.status === 422 &&
                Array.isArray(responseData.data) &&
                responseData.data.length > 0
            ) {
                setErrorResponse(
                    responseData.message ||
                        "Import gagal: terdapat error pada file Excel.",
                    responseData.data,
                );
            } else {
                errorMessage.value =
                    responseData.message ||
                    "Import failed. Please check your file and try again.";
                errorRows.value = [];
            }
        } else if (err.request) {
            errorMessage.value =
                "Network error. Please check your connection and try again.";
            errorRows.value = [];
        } else {
            errorMessage.value =
                "An unexpected error occurred. Please try again.";
            errorRows.value = [];
        }
    } finally {
        loading.value = false;
    }
};

const setErrorResponse = (
    message: string | undefined,
    data: ImportErrorRow[] | null | undefined,
) => {
    errorMessage.value = message || "Import failed. Please try again.";
    if (Array.isArray(data) && data.length > 0) {
        errorRows.value = data;
    } else {
        errorRows.value = [];
    }
};

const downloadTemplate = () => {
    const templateUrl = "/template-excel/TEMPLATE_IMPORT_QUESTION_BANK.xlsx";
    const link = document.createElement("a");
    link.href = templateUrl;
    link.download = "TEMPLATE_IMPORT_QUESTION_BANK.xlsx";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const closeModal = () => {
    emit("close");
    selectedFile.value = null;
    clearErrors();
    loading.value = false;
    if (fileInput.value) {
        fileInput.value.value = "";
    }
};
</script>
